import { useCallback } from "react"
import { Settings as ReactSlickProps } from "react-slick"
import {
  BoxProps,
  Heading,
  HStack,
  Image,
  StyleProps,
  Text,
  VStack,
} from "@chakra-ui/react"
import { DepositForAccount } from "@mezo/api"
import { MigratableToken, SupportedTokenName } from "@mezo/sdk"

import tbtcTokenWhiteOutlineLogo from "#/assets/tokens/tbtc-token-white-outline.svg"
import wbtcTokenOrangeLogo from "#/assets/tokens/wbtc-token-orange.svg"
import CurrencySymbol from "#/shared/components/CurrencySymbol"
import { FRIENDLY_DECIMALS_AMOUNT } from "#/shared/constants"
import { bigIntToHumanReadableFormat } from "#/shared/utils"
import DepositCarousel from "#/ui-old/DepositCarousel"
import DepositCarouselItemContainer from "#/ui-old/DepositCarousel/DepositCarouselItemContainer"

type DepositsCarouselProps = BoxProps &
  Pick<ReactSlickProps, "centerPadding"> & {
    mainLabelText?: string
    deposits: DepositForAccount[]
    initialDepositUuid?: string
    onItemChange?: (previousIndex: number, nextIndex: number) => void
  }

type MigratableTokenToLogo = {
  [key in MigratableToken]: string
} & {
  [key in Exclude<SupportedTokenName, MigratableToken>]?: string
}

const migratableTokenToLogo: MigratableTokenToLogo = {
  WBTC: wbtcTokenOrangeLogo,
}

export default function DepositMigrationCarousel({
  mainLabelText,
  deposits,
  initialDepositUuid,
  centerPadding,
  onItemChange,
  ...props
}: DepositsCarouselProps) {
  const renderDepositItem = useCallback(
    (
      deposit: DepositForAccount,
      isActive: boolean,
      mainItemContainerProps: StyleProps,
    ) => {
      const { amount, id, token } = deposit

      const bigIntDepositAmount = BigInt(amount)

      const userDepositAmount = bigIntToHumanReadableFormat(
        bigIntDepositAmount,
        18,
        FRIENDLY_DECIMALS_AMOUNT,
      )

      const migratableTokenImage = migratableTokenToLogo[token] || undefined

      return (
        <DepositCarouselItemContainer
          key={`migration-carousel-deposit-${id}`}
          isActive={isActive}
          mb={deposits.length > 1 ? "24px" : 0}
          {...mainItemContainerProps}
        >
          <HStack justifyContent="space-between">
            <VStack alignItems="start">
              <Heading variant="h3">{userDepositAmount}</Heading>
              <HStack>
                <Image src={migratableTokenImage} w="20px" h="20px" />
                <Text>
                  <CurrencySymbol token="WBTC" />
                </Text>
              </HStack>
            </VStack>
            <Heading variant="h2">→</Heading>
            <VStack alignItems="end">
              <Heading variant="h3">{userDepositAmount}</Heading>
              <HStack>
                <Text>
                  <CurrencySymbol token="tBTC" />
                </Text>
                <Image src={tbtcTokenWhiteOutlineLogo} w="20px" h="20px" />
              </HStack>
            </VStack>
          </HStack>
        </DepositCarouselItemContainer>
      )
    },
    [deposits.length],
  )

  return (
    <DepositCarousel
      mainLabelText={mainLabelText}
      deposits={deposits}
      initialDepositUuid={initialDepositUuid}
      centerPadding={centerPadding}
      onItemChange={onItemChange}
      renderDepositItem={renderDepositItem}
      {...props}
    />
  )
}
